// ============================================================================
// Registre CANONIQUE des types de voie — source de vérité unique du frontend.
//
// Pourquoi : le "type de voie" existait sous plusieurs formats incompatibles
// (code minuscule `avn`, MAJUSCULE MISIV `AVN`, libellé `Avenue`, variantes
// legacy `che` vs `chm`, `ave` vs `avn`, mojibake `All�e`…), répartis entre
// 2 `getAdresseTypeVoie` divergents (front/back) et 9 dropdowns hardcodés.
// Résultat : une valeur produite d'un côté ne matchait pas l'autre → « Inconnu »
// (mail François « Bug adresse DA »).
//
// Ici : UN registre. `code` = code canonique interne (= valeur des dropdowns de
// l'app). `aliases` = toutes les formes acceptées EN LECTURE (résolues vers
// `code`) : libellés Pipedrive/Google ("Avenue"), anciens codes ("chm" vs "che",
// "ave" vs "avn"), abréviations OCR ("BD", "AV."), casse/accents quelconques.
// Ça ne casse aucune donnée déjà stockée.
//
// ⚠️ IMPORTANT : ces codes sont ceux de l'APP/des dropdowns, PAS forcément les
// codes MISIV. La conversion code app -> code MISIV (pour le XML) est un problème
// BACKEND distinct, à figer une fois les codes MISIV officiels confirmés. On ne
// met donc volontairement PAS de code MISIV ici (le front ne génère pas le XML).
// ============================================================================

export interface VoieType {
  /** Code canonique interne = valeur du dropdown de l'app (minuscule). */
  code: string;
  /** Libellé affiché. */
  label: string;
  /** Formes acceptées en lecture (libellés, anciens codes, abréviations OCR). */
  aliases: string[];
}

export const UNKNOWN_VOIE_CODE = 'sans';

// Ordre = ordre d'affichage dans les dropdowns.
// NB: code Chemin = 'che' (valeur déjà utilisée par les 9 dropdowns + CTVO +
// normalizeTypeVoie). 'chm' est accepté en alias. On ne change pas ce qui est
// stocké/soumis tant que les codes MISIV ne sont pas validés.
export const VOIE_TYPES: VoieType[] = [
  { code: 'rue', label: 'Rue', aliases: ['r'] },
  { code: 'avn', label: 'Avenue', aliases: ['ave', 'av'] },
  { code: 'all', label: 'Allée', aliases: ['allee'] },
  { code: 'blvd', label: 'Boulevard', aliases: ['bd', 'bld', 'boul', 'blv'] },
  { code: 'chau', label: 'Chaussée', aliases: ['chaussee', 'chs'] },
  { code: 'che', label: 'Chemin', aliases: ['chm', 'ch', 'chem'] },
  { code: 'cour', label: 'Cour', aliases: ['cours'] },
  { code: 'fbg', label: 'Faubourg', aliases: ['faubourg', 'faub', 'fg'] },
  { code: 'fort', label: 'Fort', aliases: [] },
  { code: 'imp', label: 'Impasse', aliases: ['impasse'] },
  { code: 'ldit', label: 'Lieu-Dit', aliases: ['lieudit', 'lieu-dit', 'lieutdit', 'lieu dit', 'lieut-dit', 'ld'] },
  { code: 'parc', label: 'Parc', aliases: [] },
  { code: 'pass', label: 'Passage', aliases: ['passage', 'pas', 'psge', 'psg'] },
  { code: 'plc', label: 'Place', aliases: ['place', 'pl'] },
  { code: 'quai', label: 'Quai', aliases: [] },
  { code: 'rte', label: 'Route', aliases: ['route'] },
  { code: 'sent', label: 'Sentier', aliases: ['sentier'] },
  { code: 'squa', label: 'Square', aliases: ['square', 'sq'] },
];

/** Options pour les dropdowns "type de voie" (inclut l'option Inconnu en fin). */
export const ADDRESS_TYPE_OPTIONS = [
  ...VOIE_TYPES.map(({ code, label }) => ({ code, label })),
  { code: UNKNOWN_VOIE_CODE, label: 'Inconnu' },
] as const;

/** Minuscule, sans accents, espaces compressés — pour comparer de façon robuste. */
const normalize = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

// Map de TOUTES les formes acceptées (normalisées) -> code canonique.
const FORM_TO_CODE = new Map<string, string>();
for (const t of VOIE_TYPES) {
  for (const form of [t.code, t.label, ...t.aliases]) {
    FORM_TO_CODE.set(normalize(form), t.code);
  }
}
FORM_TO_CODE.set(normalize('Inconnu'), UNKNOWN_VOIE_CODE);
FORM_TO_CODE.set(normalize('Autre'), UNKNOWN_VOIE_CODE);
FORM_TO_CODE.set(UNKNOWN_VOIE_CODE, UNKNOWN_VOIE_CODE);

/**
 * Résout n'importe quelle forme stockée/saisie (code app, libellé Pipedrive,
 * ancien code legacy, abréviation OCR, casse/accents quelconques) vers le code
 * canonique du dropdown. Renvoie 'sans' si non reconnu.
 */
export const resolveVoieCode = (raw: string | null | undefined): string => {
  if (!raw) return UNKNOWN_VOIE_CODE;
  return FORM_TO_CODE.get(normalize(raw)) ?? UNKNOWN_VOIE_CODE;
};

/**
 * Sépare une route Pipedrive ("Avenue Ambroise Croizat") en { typeCode, name }.
 * Comparaison mot-à-mot insensible casse/accents. Gère "Lieu-Dit" (un seul token).
 * Si le 1er mot n'est pas un type connu : { typeCode: 'sans', name: route entière }
 * (aucune troncature — on ne perd pas d'information).
 */
export const splitRoute = (route: string | null | undefined): { typeCode: string; name: string } => {
  const raw = (route ?? '').trim();
  if (!raw) return { typeCode: '', name: '' };
  const words = raw.split(/\s+/);
  const firstCode = FORM_TO_CODE.get(normalize(words[0]));
  if (firstCode && firstCode !== UNKNOWN_VOIE_CODE) {
    return { typeCode: firstCode, name: words.slice(1).join(' ').trim() };
  }
  return { typeCode: UNKNOWN_VOIE_CODE, name: raw };
};

export interface StructuredOrgAddress {
  address_street_number?: string | null;
  address_route?: string | null;
  address_postal_code?: string | null;
  address_locality?: string | null;
  address_country?: string | null;
}

export interface DerivedAddress {
  number: string;
  typeCode: string;
  name: string;
  postalCode: string;
  city: string;
  country: string;
}

export interface ParsedAddress {
  number: string;
  typeCode: string;
  name: string;
  postalCode: string;
  city: string;
  country: string;
}

/**
 * Fallback LEGACY : parse une adresse en TEXTE LIBRE (cas où Pipedrive n'a pas
 * de sous-champs structurés — adresse non validée Google Maps, saisie brute).
 * Format usuel "num type nom, CP ville, pays" (séparé par virgules).
 * On isole d'abord le suffixe « , CP ville, pays » pour ne PAS le laisser dans le
 * nom de voie, puis on extrait numéro + type + nom sur le seul segment rue.
 * Dégrade proprement si le format est atypique (pas de virgule, pas de numéro…).
 */
export const parseFreeTextAddress = (full: string | null | undefined): ParsedAddress => {
  const empty: ParsedAddress = { number: '', typeCode: '', name: '', postalCode: '', city: '', country: '' };
  const raw = (full ?? '').trim();
  if (!raw) return empty;

  const parts = raw
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);

  const street = parts[0] ?? raw;
  let postalCode = '';
  let city = '';
  let country = '';

  // Segments après la rue : on cherche "CP ville" (5 chiffres + texte) et le pays.
  for (let i = 1; i < parts.length; i++) {
    const cpVille = parts[i].match(/^(\d{5})\s+(.+)$/);
    if (cpVille) {
      postalCode = cpVille[1];
      city = cpVille[2];
    } else if (/^\d{5}$/.test(parts[i]) && !postalCode) {
      postalCode = parts[i];
    } else if (i === parts.length - 1 && parts.length > 1) {
      country = parts[i];
    } else if (!city) {
      city = parts[i];
    }
  }

  // Segment rue : "12 rue lavoisier" -> numéro + route.
  const numMatch = street.match(/^\s*(\d+[a-zA-Z]?)\s+(.+)$/);
  const number = numMatch ? numMatch[1] : '';
  const route = numMatch ? numMatch[2] : street;
  const { typeCode, name } = splitRoute(route);

  return { number, typeCode, name, postalCode, city, country };
};

/** Vrai si l'org porte des sous-champs d'adresse structurés exploitables. */
export const hasStructuredAddress = (org: StructuredOrgAddress | null | undefined): boolean =>
  Boolean(org && ((org.address_route ?? '').trim() || (org.address_street_number ?? '').trim()));

/** Dérive l'adresse depuis les sous-champs structurés Pipedrive (validés Google Maps). */
export const deriveAddressFromStructuredOrg = (org: StructuredOrgAddress): DerivedAddress => {
  const { typeCode, name } = splitRoute(org.address_route);
  return {
    number: (org.address_street_number ?? '').trim(),
    typeCode,
    name,
    postalCode: (org.address_postal_code ?? '').trim(),
    city: (org.address_locality ?? '').trim(),
    country: (org.address_country ?? '').trim(),
  };
};
